import { SCol, SIcon, SPagination, SRow } from "@avalara/skylab-sdk/react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { getStatesAsync } from "../../app/commonSlice";
import {
    EcmsRecord,
    fetchEcms,
    revokeEcmsCert,
    selectEcms,
    selectEcmsError,
    selectEcmsIsLoading,
    selectHasFilterChips,
    selectSortColumn,
    setSortColumn,
} from "../../app/ecmsSlice";
import { selectSession, updateRowCountPreference } from "../../app/sessionSlice";
import { SessionState } from "../../app/sessionSlice/types";
import { AppDispatch } from "../../app/types";
import toast from "../../hooks/toast";
import { useGridScroll } from "../../hooks/useGridScroll";
import DataGrid from "../../shared/dataGrid/DataGrid";
import { GridHeader } from "../../shared/dataGrid/types";
import { getPageTitle } from "../../shared/sessionUtility";
import { getRowsPerPage } from "../../shared/Utils";
import "../search/shared-grid-sticky.css";
import Loader from "../sharedComponents/Loader";
import "./ecms.css";
import ECMSFilter from "./ECMSFilter";

// Types
interface PaginationEvent {
    detail: {
        currentPage: number;
        startIndex: number;
        endIndex: number;
        rowsPerPage: number;
        flag: string;
    };
}

function ECMS(): React.JSX.Element {
    const dispatch = useDispatch<AppDispatch>();
    const ecms = useSelector(selectEcms);
    const ecmsData: EcmsRecord[] = ecms.data;
    const paginateData = ecms.paginate;
    const isLoading = useSelector(selectEcmsIsLoading);
    const error = useSelector(selectEcmsError);
    const { rowsPerPage } = useSelector(selectSession, shallowEqual) as SessionState;
    const [pageTitle, setPageTitle] = useState<string>("ECMS");
    const sortColumn = useSelector(selectSortColumn);
    const hasFilterChips = useSelector(selectHasFilterChips);
    const [openFilters, setOpenFilters] = useState(false);
    const [showToast] = toast();

    const { handleScroll: handleGridScroll } = useGridScroll({
        wrapperClass: "ecms-grid-scrollable",
        trackLeftScroll: true,
        trackRightScroll: false,
        hasData: (ecmsData?.length ?? 0) > 0,
        isLoading,
        dependencies: [ecmsData?.length, isLoading],
    });

    const toggleFilters = (): void => {
        setOpenFilters(!openFilters);
    };

    useEffect(() => {
        if (isLoading) return;
        if ((ecmsData?.length ?? 0) > 0) return;
        if (paginateData?.startIndex >= 0) return;
        dispatch(fetchEcms());
        // Fetch states for region filter - includes US and Canada states
        dispatch(getStatesAsync(false));
    }, [dispatch, isLoading, ecmsData?.length, paginateData?.startIndex]);

    useEffect(() => {
        const title = dispatch(getPageTitle("ECMS")) as unknown;
        if (typeof title === "string") {
            setPageTitle(title);
        }
    }, [dispatch]);

    // Show error toast when API fails
    useEffect(() => {
        if (error) {
            showToast("error", `Failed to load ECMS data: ${error}`, "ecms-error-toast");
        }
    }, [error, showToast]);

    const handlePaginate = (e: PaginationEvent): void => {
        if (e.detail.currentPage !== 0 && paginateData?.totalRecords > 0 && e.detail.flag !== "") {
            if (e.detail.rowsPerPage !== rowsPerPage) {
                dispatch(updateRowCountPreference(e.detail.rowsPerPage));
            }
            dispatch(
                fetchEcms({
                    filterString: "",
                    top: e?.detail?.rowsPerPage,
                    skip: e?.detail?.startIndex,
                    paginateDetails: {
                        pageNumber: e?.detail?.currentPage,
                        startIndex: e?.detail?.startIndex,
                        endIndex: e?.detail?.endIndex,
                        rowsPerPage: e?.detail?.rowsPerPage,
                    },
                })
            );
        }
    };

    const sortFunction = (columnId: string): void => {
        const order = sortColumn[0] === columnId ? !sortColumn[1] : true;
        dispatch(setSortColumn([columnId, order]));
        dispatch(
            fetchEcms({
                filterString: "",
                top: rowsPerPage,
                skip: 0,
            })
        );
    };

    const handleEcmsCertificateRevocation = async (ecmsCertId: string): Promise<void> => {
        if (!ecmsCertId) return;
        try {
            await dispatch(
                revokeEcmsCert({
                    ecmsCertId,
                    auditMessage: "ECM Certificate Revoke",
                })
            ).unwrap();
            showToast("success", "ECMS certificate revoked successfully", "ecms-success-toast");
        } catch {
            showToast("error", "Failed to revoke ECMS certificate", "ecms-error-toast");
        }
        dispatch(fetchEcms());
    };

    const gridHeaders: GridHeader<EcmsRecord>[] = [
        { id: "customerName", label: "Customer Name", sortable: true },
        { id: "customerCode", label: "Customer Code", sortable: true },
        { id: "avaCertId", label: "Cert ID", sortable: true },
        { id: "exemptCertId", label: "Ecms Cert ID", sortable: true },
        { id: "country", label: "Country" },
        {
            id: "regionsApplicable",
            label: "Regions",
            render: (data: EcmsRecord) => {
                const value =
                    "regionsApplicable" in data
                        ? (data as EcmsRecord & { regionsApplicable?: string }).regionsApplicable
                        : data.region;
                return <>{value?.split(",")?.join(", ") ?? ""}</>;
            },
        },
        { id: "exemptReasonDescription", label: "Exemption Reason" },
        { id: "businessTypeDescription", label: "Business Type" },
        { id: "exemptCertStatusId", label: "Status" },
        {
            id: "actions",
            label: null,
            render: (data: EcmsRecord) => (
                <>
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => handleEcmsCertificateRevocation(data.exemptCertId)}
                        data-testid={`${testIDs.ecms.grid}-revoke-button-${data.exemptCertId}`}>
                        Revoke
                    </button>
                </>
            ),
        },
    ];

    let pagination: React.JSX.Element | null = null;
    if (paginateData?.totalRecords > 0) {
        pagination = (
            <SPagination
                className="margin-top-md"
                rowsPerPage={getRowsPerPage(paginateData, rowsPerPage) as number}
                totalRecords={paginateData?.totalRecords}
                startIndex={paginateData?.startIndex}
                onS-paginate={(e: PaginationEvent) => {
                    handlePaginate(e);
                }}
            />
        );
    }

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <h1>ECMS</h1>
            {!isLoading && (
                <button
                    type="button"
                    className="secondary margin-bottom-md ecms-filters-button"
                    onClick={toggleFilters}
                    data-testid={testIDs.ecms.filtersButton}>
                    <SIcon name="filter" className="margin-right-xs" aria-hidden="true" />
                    Filters
                </button>
            )}
            <SRow>
                <SCol span="12">
                    <ECMSFilter
                        hasFilterChips={hasFilterChips}
                        openFilters={openFilters}
                        onToggleFilters={toggleFilters}
                    />
                    {isLoading ? (
                        <Loader loading={isLoading} />
                    ) : (
                        <>
                            <DataGrid
                                gridHeaders={gridHeaders}
                                gridData={ecmsData}
                                sortFunction={sortFunction}
                                gridName="records"
                                totalRecords={paginateData?.totalRecords}
                                sortColumn={sortColumn}
                                hasFilterChips={hasFilterChips}
                                usesFilterChips
                                scrollable
                                scrollableClassName="ecms-grid-scrollable"
                                onScroll={handleGridScroll}
                                errorMessage={
                                    error
                                        ? "Unable to load ECMS data. Please try again or contact support if the issue persists."
                                        : undefined
                                }
                                testId={testIDs.ecms.grid}
                            />
                            {pagination}
                        </>
                    )}
                </SCol>
            </SRow>
        </>
    );
}

export default ECMS;
